import {
    QuoteData,
    QuotesCallback
} from '../charting-library/datafeed-api';

import {
    getErrorMessage,
    logMessage
} from './helpers';

import {QuotesProvider} from './quotes-provider';

interface QuoteSubscriber {
    symbols: string[];
    fastSymbols: string[];
    listener: QuotesCallback;
}

interface QuoteSubscribers {
    [listenerId: string]: QuoteSubscriber;
}

const enum SymbolsType {
    General,
    Fast
}

const enum UpdateTimeouts {
    Fast = 10 * 1000,
    General = 60 * 1000
}

export class QuotesPulseProvider {
    private readonly quotesProvider: QuotesProvider;
    private readonly subscribers: QuoteSubscribers = {};
    private requestsPending: number = 0;

    public constructor(quotesProvider: QuotesProvider) {
        this.quotesProvider = quotesProvider;

        setInterval(this.updateQuotes.bind(this, SymbolsType.Fast), UpdateTimeouts.Fast);
        setInterval(this.updateQuotes.bind(this, SymbolsType.General), UpdateTimeouts.General);
    }

    public subscribeQuotes(
        symbols: string[],
        fastSymbols: string[],
        onRealtimeCallback: QuotesCallback,
        listenerGuid: string
    ): void {
        this.subscribers[listenerGuid] = {
            symbols,
            fastSymbols,
            listener: onRealtimeCallback
        };

        logMessage(`QuotesPulseProvider: subscribed quotes with #${listenerGuid}`);
    }

    public unsubscribeQuotes(listenerGuid: string): void {
        delete this.subscribers[listenerGuid];
        logMessage(`QuotesPulseProvider: unsubscribed quotes with #${listenerGuid}`);
    }

    private updateQuotes(updateType: SymbolsType): void {
        if (this.requestsPending > 0) {
            return;
        }

        Object.keys(this.subscribers).forEach(listenerGuid => {
            this.requestsPending++;

            const message = `QuotesPulseProvider: data for #${listenerGuid} (${updateType}) updated`;
            const subscriptionRecord = this.subscribers[listenerGuid];
            this.quotesProvider.getQuotes(updateType === SymbolsType.Fast
                ? subscriptionRecord.fastSymbols
                : subscriptionRecord.symbols)
                .then((data: QuoteData[]) => {
                    this.requestsPending--;
                    if (!this.subscribers[listenerGuid]) {
                        return;
                    }

                    subscriptionRecord.listener(data);
                    logMessage(`${message} successfully, pending=${this.requestsPending}`);
                })
                .catch((reason?: string | Error) => {
                    this.requestsPending--;
                    logMessage(`${message} with error=${getErrorMessage(reason)}, pending=${this.requestsPending}`);
                });
        });
    }
}
